import { ExamLocationRequestStatusLabel } from '@exam-center/exam-location-management';
import { ExamLocationRequestWorkflowStepCode } from '@exam-center/graphql';
import {
  CancelOutlinedIcon,
  CheckCircleOutlineRoundedIcon,
  ChipGroup,
  EllipsisText,
  Flex,
  PendingOutlinedIcon,
  Text,
  type GridColDef,
} from '@shared/components';
import { formatDateTime } from '@shared/utils';

import type {
  ExamLocationVerdict,
  InReviewExamLocationRequest,
  RejectedExamLocationRequest,
} from './types';

function renderVerdictResult(verdict: ExamLocationVerdict | null) {
  let content;
  switch (verdict) {
    case 'APPROVED':
      content = (
        <>
          <CheckCircleOutlineRoundedIcon sx={{ color: (theme) => theme.palette.success.main }} />
          <Text>อนุมัติแล้ว</Text>
        </>
      );
      break;
    case 'REJECTED':
      content = (
        <>
          <CancelOutlinedIcon sx={{ color: (theme) => theme.palette.error.main }} />
          <Text>ไม่อนุมัติ</Text>
        </>
      );
      break;
    case 'PENDING':
      content = (
        <>
          <PendingOutlinedIcon sx={{ color: (theme) => theme.palette.warning.main }} />
          <Text>รออนุมัติ</Text>
        </>
      );
      break;
    default:
      content = <Text>-</Text>;
  }

  return <Flex sx={{ gap: (theme) => theme.spacing(2) }}>{content}</Flex>;
}

function renderFirstInReviewStepResult(step: ExamLocationRequestWorkflowStepCode | null) {
  let content;
  switch (step) {
    case ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_2:
      content = (
        <>
          <CheckCircleOutlineRoundedIcon sx={{ color: (theme) => theme.palette.success.main }} />
          <Text>อนุมัติแล้ว</Text>
        </>
      );
      break;
    case ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_1:
      content = (
        <>
          <PendingOutlinedIcon sx={{ color: (theme) => theme.palette.warning.main }} />
          <Text>รออนุมัติ</Text>
        </>
      );
      break;
    default:
      content = <Text>-</Text>;
  }

  return <Flex sx={{ gap: (theme) => theme.spacing(2) }}>{content}</Flex>;
}

function renderSecondInReviewStepResult(step: ExamLocationRequestWorkflowStepCode | null) {
  let content;
  switch (step) {
    case ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_2:
      content = (
        <>
          <PendingOutlinedIcon sx={{ color: (theme) => theme.palette.warning.main }} />
          <Text>รออนุมัติ</Text>
        </>
      );
      break;
    default:
      content = <Text>-</Text>;
  }

  return <Flex sx={{ gap: (theme) => theme.spacing(2) }}>{content}</Flex>;
}

export const inReviewColumns: GridColDef<InReviewExamLocationRequest>[] = [
  {
    field: 'refCode',
    headerName: 'เลขที่คำขอ',
    width: 100,
    sortable: false,
    align: 'left',
    renderCell: ({ row }) => {
      return <Text>{row.refCode ?? '-'}</Text>;
    },
  },
  {
    field: 'province',
    headerName: 'จังหวัด',
    width: 144,
    sortable: false,
    renderCell: ({ row }) => {
      return <EllipsisText>{row.province}</EllipsisText>;
    },
  },
  {
    field: 'locationName',
    headerName: 'สถานที่สอบ / อาคาร / ชั้น',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <Flex sx={{ flexDirection: 'column' }}>
          <EllipsisText variant='body1Semibold'>{row.locationName}</EllipsisText>
          <EllipsisText>
            อาคาร: {row.buildingName} ชั้น: {row.floor}
          </EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'roomName',
    headerName: 'ห้องสอบ',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <Flex sx={{ flexDirection: 'column' }}>
          <EllipsisText>ห้อง: {row.roomName}</EllipsisText>
          <EllipsisText>เลขที่: {row.roomNumber}</EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'examCenter',
    headerName: 'ศูนย์ทดสอบ',
    width: 110,
    sortable: false,
    renderCell: ({ row }) => {
      return <ChipGroup labels={[row.examCenter]} limit={3} direction='row' />;
    },
  },
  {
    field: 'status',
    headerName: 'สถานะ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <ExamLocationRequestStatusLabel variant={row.status} />;
    },
  },
  {
    field: 'firstVerdict',
    headerName: 'รอบการอนุมัติที่ 1',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return renderFirstInReviewStepResult(row.step);
    },
  },
  {
    field: 'secondVerdict',
    headerName: 'รอบการอนุมัติที่ 2',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return renderSecondInReviewStepResult(row.step);
    },
  },
  {
    field: 'requestedAt',
    headerName: 'วันที่ส่งคำขอ',
    width: 160,
    renderCell: ({ row }) => {
      return <Text>{row.requestedAt ? formatDateTime(row.requestedAt) : '-'}</Text>;
    },
  },
];

export const rejectedColumns: GridColDef<RejectedExamLocationRequest>[] = [
  {
    field: 'refCode',
    headerName: 'เลขที่คำขอ',
    width: 100,
    sortable: false,
    align: 'left',
    renderCell: ({ row }) => {
      return <Text>{row.refCode ?? '-'}</Text>;
    },
  },
  {
    field: 'province',
    headerName: 'จังหวัด',
    width: 144,
    sortable: false,
    renderCell: ({ row }) => {
      return <EllipsisText maxLines={1}>{row.province}</EllipsisText>;
    },
  },
  {
    field: 'locationName',
    headerName: 'สถานที่สอบ / อาคาร / ชั้น',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <Flex sx={{ flexDirection: 'column' }}>
          <EllipsisText maxLines={1} variant='body1Semibold'>
            {row.locationName}
          </EllipsisText>
          <EllipsisText maxLines={1}>
            อาคาร: {row.buildingName} ชั้น: {row.floor}
          </EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'roomName',
    headerName: 'ห้องสอบ',
    flex: 1,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <Flex sx={{ flexDirection: 'column' }}>
          <EllipsisText maxLines={1}>ห้อง: {row.roomName}</EllipsisText>
          <EllipsisText maxLines={1}>เลขที่: {row.roomNumber}</EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'examCenter',
    headerName: 'ศูนย์ทดสอบ',
    width: 110,
    sortable: false,
    renderCell: ({ row }) => {
      return <ChipGroup labels={[row.examCenter]} limit={3} direction='row' />;
    },
  },
  {
    field: 'status',
    headerName: 'สถานะ',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return <ExamLocationRequestStatusLabel variant={row.status} />;
    },
  },
  {
    field: 'firstVerdict',
    headerName: 'รอบการอนุมัติที่ 1',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return renderVerdictResult(row.firstReviewerVerdict);
    },
  },
  {
    field: 'secondVerdict',
    headerName: 'รอบการอนุมัติที่ 2',
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      return renderVerdictResult(row.secondReviewerVerdict);
    },
  },
  {
    field: 'rejectedAt',
    headerName: 'วันที่ไม่อนุมัติ',
    width: 160,
    renderCell: ({ row }) => {
      return <Text>{row.rejectedAt ? formatDateTime(row.rejectedAt) : '-'}</Text>;
    },
  },
];
